'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronRight } from 'lucide-react';
import { SettingsCard, PickerRow } from '@/shared/ui';
import { ThumbStrip } from '../scrollable-settings/ReferenceSummary';
import { ProjectReferenceSheet } from '../scrollable-settings/ProjectReferenceSheet';
import {
  useClassifiedProjectReferences,
  useProjectReferences,
} from '@/features/project-reference/hooks/use-project-references';
import type {
  ProjectReferenceCategory,
  ProjectReferenceAspect,
} from '@/types/project-reference';
import { hasAspect } from '@/types/project-reference';

interface ReferenceSourceGroupProps {
  projectId: string;
  category: ProjectReferenceCategory;
  /** Manually selected project-reference ids (drives the manual grid). */
  selectedIds: string[];
  /** Whether automatic (use all aspect refs) is active (`use_auto_*_refs`). */
  useAllRefs: boolean;
  maxSelection: number;
  /** "No references": clear `use_auto_*_refs` + manual refs. Library untouched. */
  onSelectNone: () => void;
  /** "Automatic": set `use_auto_*_refs` + clear manual refs + clear library. */
  onSelectAll: () => void;
  /** Manual selection change (also clears library in the caller). */
  onSelectiveChange: (ids: string[]) => void;
  onNavigateToReferences?: () => void;
}

/**
 * "From reference" group for the Style/Materials/Colors sub-panels, simplified
 * to two options: "No references" and a single "From references" drill-down.
 * The drill-down opens {@link ProjectReferenceSheet}, which now hosts the whole
 * reference workflow — Automatic (use all), a grid of every inspiration
 * reference for manual picking, and inline upload. Mirrors Apple
 * `ReferenceSourceGroup` and Android `ReferenceSourceGroup`.
 *
 * Mode is derived from existing state — no new fields:
 *  - manual refs picked → "From references" (selected)
 *  - else `useAllRefs` true → "From references" (selected, Automatic inside)
 *  - else → "No references" (default)
 */
export function ReferenceSourceGroup({
  projectId,
  category,
  selectedIds,
  useAllRefs,
  maxSelection,
  onSelectNone,
  onSelectAll,
  onSelectiveChange,
  onNavigateToReferences,
}: ReferenceSourceGroupProps) {
  const t = useTranslations('render.settings.projectReference');
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  // Automatic-pool preview: every classified inspiration image carrying THIS
  // axis's aspect. `category` is style/material/color here → maps 1:1 to aspect.
  const { data: classifiedReferences = [] } = useClassifiedProjectReferences(
    projectId,
    undefined,
    { enabled: !!projectId }
  );
  const allRefImages = classifiedReferences
    .filter((r) => hasAspect(r, category as ProjectReferenceAspect))
    .map((r) => r.attributes.file_url)
    .filter((url): url is string => !!url);

  // Selected references resolve by id across ALL references (any category): the
  // picker lets users select refs from other/unclassified categories.
  const { data: allRefs = [] } = useProjectReferences(projectId, {
    enabled: !!projectId,
  });
  const selectedRefImages = selectedIds
    .map((id) => allRefs.find((r) => r.id === id)?.attributes.file_url)
    .filter((url): url is string => !!url);

  const mode =
    selectedIds.length > 0 ? 'selective' : useAllRefs ? 'all' : 'none';

  // Thumbnails on the "From references" row: the manual selection if any,
  // otherwise the auto pool when Automatic is active.
  const fromImages =
    selectedRefImages.length > 0
      ? selectedRefImages
      : useAllRefs
        ? allRefImages
        : [];

  return (
    <>
      <SettingsCard>
        <PickerRow
          title={t('none')}
          isSelected={mode === 'none'}
          onClick={onSelectNone}
        />
        {/* Single drill-down: opens the reference picker panel (Automatic +
            manual grid + upload). Selected whenever the axis uses references. */}
        <PickerRow
          title={t('fromReference')}
          isSelected={mode !== 'none'}
          onClick={() => setIsSheetOpen(true)}
          accessory={
            <div className="flex items-center gap-2">
              {fromImages.length > 0 && (
                <ThumbStrip images={fromImages} shape="circle" />
              )}
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
            </div>
          }
        />
      </SettingsCard>

      <ProjectReferenceSheet
        projectId={projectId}
        category={category}
        selectedIds={selectedIds}
        onSelectionChange={onSelectiveChange}
        useAllRefs={useAllRefs}
        onSelectAll={onSelectAll}
        maxSelection={maxSelection}
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        onNavigateToReferences={onNavigateToReferences}
      />
    </>
  );
}
